import { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const base = (id) => `${API}/api/trade-network/partner/${id}`;
const errOf = async (r) => {
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Something went wrong. Please try again.");
  return d;
};

const STATUS_TEXT = {
  valid: "Insurance on file",
  expiring: "Insurance expires soon",
  expired: "Insurance expired",
  missing: "Insurance certificate needed",
};

export const InsuranceCard = ({ p, onSaved }) => {
  const [file, setFile] = useState(null);
  const [exp, setExp] = useState("");
  const [carrier, setCarrier] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const st = p.insurance_status || "missing";

  const submit = async (e) => {
    e.preventDefault(); setMsg(""); setBusy(true);
    try {
      const f = new FormData();
      f.append("file", file); f.append("expires_on", exp); f.append("carrier", carrier);
      await errOf(await fetch(`${base(p.partner_id)}/insurance`, { method: "POST", body: f }));
      setMsg("Certificate received. Thank you."); setFile(null); onSaved();
    } catch (x) { setMsg(x.message); } finally { setBusy(false); }
  };

  return (
    <form className="pw-card" onSubmit={submit} data-testid="partner-insurance-card">
      <h2 className="pw-h2">Insurance certificate</h2>
      <div className={`pw-pill pw-pill--${st}`} data-testid="partner-insurance-status">
        {STATUS_TEXT[st]}{p.insurance_expires ? ` · expires ${p.insurance_expires}` : ""}
      </div>
      <p className="pw-p pw-muted">Work orders are released only to partners with current general liability coverage. Upload a PDF or photo of your certificate (COI).</p>
      <div className="pw-grid">
        <label className="pw-field">Certificate (PDF or photo)
          <input type="file" accept="application/pdf,image/*" required onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="partner-coi-file" />
        </label>
        <label className="pw-field">Policy expiration date
          <input type="date" required value={exp} onChange={(e) => setExp(e.target.value)} data-testid="partner-coi-expires" />
        </label>
        <label className="pw-field">Insurance carrier (optional)
          <input value={carrier} onChange={(e) => setCarrier(e.target.value)} data-testid="partner-coi-carrier" />
        </label>
      </div>
      {msg && <div className="pw-note" data-testid="partner-insurance-msg">{msg}</div>}
      <button className="pw-btn" disabled={busy || !file || !exp} data-testid="partner-coi-submit">{busy ? "Uploading…" : st === "missing" ? "Upload certificate" : "Upload updated certificate"}</button>
    </form>
  );
};

const EMPTY = { business_name: "", contact_name: "", trade: "", phone: "", email: "", city: "", note: "" };

export const ReferralCard = ({ p, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setMsg(""); setBusy(true);
    try {
      await errOf(await fetch(`${base(p.partner_id)}/refer`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(f) }));
      setMsg(`Thank you. ${f.business_name} has been sent to our partnership desk for review.`); setF(EMPTY); onSaved();
    } catch (x) { setMsg(x.message); } finally { setBusy(false); }
  };
  const field = (k, label, props = {}) => (
    <label className="pw-field">{label}<input value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`partner-ref-${k}`} {...props} /></label>
  );

  return (
    <form className="pw-card" onSubmit={submit} data-testid="partner-referral-card">
      <h2 className="pw-h2">Know a trade you'd vouch for?</h2>
      <p className="pw-p pw-muted">Refer the people you already trust on a job. Each referral is reviewed personally before an invitation goes out.{p.referral_count ? ` You've referred ${p.referral_count} so far.` : ""}</p>
      <div className="pw-grid">
        {field("business_name", "Business or person", { required: true })}
        {field("trade", "Their trade", { required: true })}
        {field("contact_name", "Contact name")}
        {field("phone", "Phone", { type: "tel" })}
        {field("email", "Email", { type: "email" })}
        {field("city", "City")}
      </div>
      {msg && <div className="pw-note" data-testid="partner-referral-msg">{msg}</div>}
      <button className="pw-btn" disabled={busy || !f.business_name || !f.trade || !(f.phone || f.email)} data-testid="partner-ref-submit">{busy ? "Sending…" : "Send referral"}</button>
    </form>
  );
};


export const RateCard = () => {
  const [d, setD] = useState(null);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-network/rate-card`).then((r) => (r.ok ? r.json() : null)).then(setD).catch(() => setD(null));
  }, []);
  if (!d) return null;
  return (
    <div className="pw-card" data-testid="partner-rate-card">
      <h2 className="pw-h2">Master rate card · DFW</h2>
      <p className="pw-p">Baseline day rates backed by multi-unit turnover volume. Materials are pre-staged on site before 06:00 on mobilization day, at documented cost with zero markup.</p>
      <ul className="pw-terms">
        {d.lanes.map((l) => <li key={l.trade_lane} data-testid={`rate-card-${l.trade_lane}`}>{l.label}: <b>${l.day_rate_usd.toLocaleString()}</b> / day</li>)}
      </ul>
      <p className="pw-p pw-muted">Bids above 1.2x the baseline are reviewed by the CEO before award.</p>
    </div>
  );
};
